import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import type { WorkspaceAgentPatch, WorkspaceAgentTurn } from '../../types';
import useVoyagerStore from '../../stores/useVoyagerStore';

interface Props {
  turn: WorkspaceAgentTurn;
}

function renderList(label: string, items: string[] | undefined, color: 'success' | 'error' | 'default') {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 64 }}>
        {label}:
      </Typography>
      {items.map((s) => (
        <Chip key={`${label}-${s}`} label={s} size="small" color={color === 'default' ? undefined : color} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
      ))}
    </Box>
  );
}

function hasAnyChanges(p: WorkspaceAgentPatch): boolean {
  return Boolean(
    p.set_perspective_id ||
    (p.add_metrics && p.add_metrics.length) ||
    (p.remove_metrics && p.remove_metrics.length) ||
    p.replace_metrics ||
    (p.add_dimensions && p.add_dimensions.length) ||
    (p.remove_dimensions && p.remove_dimensions.length) ||
    p.replace_dimensions ||
    (p.add_filters && p.add_filters.length) ||
    p.replace_filters ||
    p.set_active_date_field ||
    p.set_date_range_preset ||
    p.set_date_range_start ||
    p.set_date_range_end
  );
}

const ProposedPatchCard: React.FC<Props> = ({ turn }) => {
  const apply = useVoyagerStore((s) => s.applyWorkspaceAgentPatch);
  const dismiss = useVoyagerStore((s) => s.dismissWorkspaceAgentPatch);
  const patch = turn.response?.patch;
  if (!patch || !hasAnyChanges(patch)) return null;

  const filterStrings = (arr?: { column: string; operator: string; value: string }[]) =>
    (arr || []).map((f) => `${f.column} ${f.operator} ${f.value}`);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: '12px',
        borderColor: 'var(--line)',
        bgcolor: turn.applied ? 'var(--ok-tint)' : 'var(--surface)',
        opacity: turn.dismissed ? 0.6 : 1,
        backgroundImage: 'none',
        boxShadow: 'none',
      }}
    >
      <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>
        Proposed changes
      </Typography>

      <Box sx={{ mt: 0.5 }}>
        {renderList('Add metrics', patch.add_metrics, 'success')}
        {renderList('Remove metrics', patch.remove_metrics, 'error')}
        {renderList('Set metrics', patch.replace_metrics || undefined, 'default')}
        {renderList('Add dims', patch.add_dimensions, 'success')}
        {renderList('Remove dims', patch.remove_dimensions, 'error')}
        {renderList('Set dims', patch.replace_dimensions || undefined, 'default')}
        {renderList('Add filters', filterStrings(patch.add_filters), 'success')}
        {renderList('Set filters', patch.replace_filters ? filterStrings(patch.replace_filters) : undefined, 'default')}
        {patch.set_active_date_field && (
          <Typography variant="caption" sx={{ display: 'block' }}>
            Date field → <b>{patch.set_active_date_field}</b>
          </Typography>
        )}
        {patch.set_date_range_preset && (
          <Typography variant="caption" sx={{ display: 'block' }}>
            Date range → <b>{patch.set_date_range_preset}</b>
          </Typography>
        )}
        {(patch.set_date_range_start || patch.set_date_range_end) && (
          <Typography variant="caption" sx={{ display: 'block' }}>
            Range: {patch.set_date_range_start || '…'} → {patch.set_date_range_end || '…'}
          </Typography>
        )}
        {patch.set_perspective_id && (
          <Typography variant="caption" sx={{ display: 'block' }}>
            Switch perspective → <b>{patch.set_perspective_id}</b>
          </Typography>
        )}
        {patch.execute && (
          <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>
            Will run the query after applying
          </Typography>
        )}
      </Box>

      {!turn.applied && !turn.dismissed && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={() => apply(turn.id)}
          >
            Apply
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => dismiss(turn.id)}
          >
            Dismiss
          </Button>
        </Box>
      )}
      {turn.applied && (
        <Typography variant="caption" sx={{ color: 'success.dark', mt: 1, display: 'block' }}>
          Applied to workspace
        </Typography>
      )}
      {turn.dismissed && (
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
          Dismissed
        </Typography>
      )}
    </Paper>
  );
};

export default ProposedPatchCard;
